import type { Control } from "./Control.js";
import type { Rect } from "../../common/geometry.js";

/**
 * A browser's password manager, on a canvas.
 *
 * Nothing Webwin draws is reachable by one: a saved credential is offered to
 * an `<input>`, by a part of the browser no page can draw or fake - the same
 * shape as a file dialog. So a sign-in form opts in, and the fields it names
 * each get a real, invisible input laid exactly over the control, inside a
 * real `<form>`. The manager fills those; `AutofillForm` mirrors what arrives
 * back into the controls, and mirrors an edit made here out to them.
 *
 * The input is also where keystrokes go while its control has focus, which is
 * the whole reason this is a *host swap* rather than a second copy of text
 * entry: `KeyboardBridge` already knows how to be attached to a field, and a
 * password manager wants the field it is attached to to be `type="password"`.
 */
export type AutofillPurpose = "username" | "current-password" | "new-password" | "one-time-code";

/** What a purpose becomes on the DOM field. */
export interface AutofillAttributes {
  type: "text" | "password";
  autocomplete: AutoFill;
  name: string;
  inputMode?: string;
}

/**
 * The one table that says what a purpose means to a password manager.
 *
 * Pure, because the difference between `current-password` and `new-password`
 * is the difference between "offer the saved one" and "offer to invent one",
 * and getting it wrong is invisible in a screenshot.
 */
export function autofillAttributes(purpose: AutofillPurpose): AutofillAttributes {
  switch (purpose) {
    case "username":
      // An email address used to sign in is a username. `autocomplete="email"`
      // asks for a contact detail, which is a different drawer of the same
      // manager and does not carry a password with it.
      return { type: "text", autocomplete: "username", name: "username" };
    case "current-password":
      return { type: "password", autocomplete: "current-password", name: "password" };
    case "new-password":
      return { type: "password", autocomplete: "new-password", name: "new-password" };
    case "one-time-code":
      return { type: "text", autocomplete: "one-time-code", name: "otp", inputMode: "numeric" };
  }
}

/** Where the invisible field goes, in CSS pixels. */
export interface OverlayBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Place the field over its control.
 *
 * It is sized to the control rather than parked in a corner because a manager
 * skips a field it decides nobody can see, and because the browser anchors its
 * own drop-down to these bounds - a saved account offered a hundred pixels
 * away from the box it fills reads as some other page's menu.
 */
export function overlayBox(bounds: Rect, uiScale: number): OverlayBox {
  const scale = uiScale > 0 ? uiScale : 1;
  return {
    left: Math.round(bounds.x * scale),
    top: Math.round(bounds.y * scale),
    width: Math.max(1, Math.round(bounds.width * scale)),
    height: Math.max(1, Math.round(bounds.height * scale)),
  };
}

/** Which way a value has to travel, and what it is. */
export type MirrorMove = { to: "control"; value: string } | { to: "field"; value: string } | null;

/** What each end of one mirror held the last time it was looked at. */
export interface MirrorSeen {
  field: string;
  control: string;
}

/**
 * Decide which way a value has to travel this frame.
 *
 * Both ends can be written without telling us - the browser fills the field,
 * the application clears the control - so each is compared against what it
 * last held rather than against the other. Comparing the two to each other
 * cannot tell "the manager filled this" from "the code cleared this", and
 * guessing means either losing a fill or putting a cleared password back.
 */
export function mirrorMove(field: string, control: string, seen: MirrorSeen): MirrorMove {
  if (field !== seen.field) return field === control ? null : { to: "control", value: field };
  if (control !== seen.control) return { to: "field", value: control };
  return null;
}

/** A control this form shadows, and what the browser should think it is for. */
export interface AutofillFieldSpec {
  control: Control & { text: string };
  purpose: AutofillPurpose;
}

interface BoundField {
  spec: AutofillFieldSpec;
  element: HTMLInputElement;
  seen: MirrorSeen;
  box: OverlayBox | null;
  visible: boolean | null;
}

/** What `AutofillForm` needs of the desktop, spelled out so a test can supply it. */
export interface AutofillHost {
  readonly uiScale: number;
  /** True while an IME is composing, when a field holds half a character. */
  readonly composing: boolean;
  invalidate(): void;
  adoptKeyboardHost(element: HTMLElement): void;
  releaseKeyboardHost(element: HTMLElement): void;
}

interface PasswordCredentialData {
  id: string;
  password: string;
  name?: string;
}

type PasswordCredentialCtor = new (data: PasswordCredentialData) => Credential;

/**
 * A live `<form>` shadowing a set of controls.
 *
 * Created through `Desktop.beginAutofill` and disposed when what it shadows
 * goes away - which is also the moment a browser decides whether to offer to
 * save what was typed, so dispose it *after* the sign-in succeeded rather
 * than before.
 */
export class AutofillForm {
  readonly element: HTMLFormElement;
  #host: AutofillHost;
  #fields: BoundField[] = [];
  #disposed = false;

  constructor(host: AutofillHost, parent: HTMLElement, specs: readonly AutofillFieldSpec[]) {
    this.#host = host;

    const form = document.createElement("form");
    form.className = "webwin-autofill";
    // It never navigates: the application is what signs in. The handler is
    // here because an Enter reaching the DOM would otherwise reload the page.
    form.addEventListener("submit", this.#onSubmit);
    Object.assign(form.style, {
      position: "absolute",
      left: "0px",
      top: "0px",
      width: "0px",
      height: "0px",
      margin: "0",
      padding: "0",
      border: "0",
      // The canvas does all the pointing. These fields are here to be filled,
      // and to be typed into once Webwin has decided what has focus.
      pointerEvents: "none",
      zIndex: "1",
    } satisfies Partial<CSSStyleDeclaration>);
    parent.appendChild(form);
    this.element = form;

    for (const spec of specs) {
      const attrs = autofillAttributes(spec.purpose);
      const input = document.createElement("input");
      input.type = attrs.type;
      input.name = attrs.name;
      input.autocomplete = attrs.autocomplete;
      if (attrs.inputMode) input.inputMode = attrs.inputMode;
      input.autocapitalize = "off";
      input.spellcheck = false;
      // Webwin's own focus order moves between the controls; a browser tab
      // stop here would be a second, invisible one interleaved with it.
      input.tabIndex = -1;
      Object.assign(input.style, {
        position: "absolute",
        margin: "0",
        padding: "0",
        border: "0",
        outline: "none",
        background: "transparent",
        color: "transparent",
        caretColor: "transparent",
        pointerEvents: "none",
        // Smaller than this and a phone zooms the page when one takes focus.
        fontSize: "16px",
        opacity: "0",
      } satisfies Partial<CSSStyleDeclaration>);
      input.value = spec.control.text;
      form.appendChild(input);
      // Adopted first, so the bridge's own handlers are in front of these two:
      // a keystroke has to become an edit before anything here looks at the
      // field, or the same text is taken twice.
      host.adoptKeyboardHost(input);
      input.addEventListener("input", this.#onFieldInput);
      input.addEventListener("change", this.#onFieldInput);
      this.#fields.push({
        spec,
        element: input,
        seen: { field: input.value, control: spec.control.text },
        box: null,
        visible: null,
      });
    }

    this.sync();
  }

  /** The field standing in for `control`, if this form shadows it. */
  fieldFor(control: Control): HTMLInputElement | null {
    return this.#fields.find((f) => f.spec.control === control)?.element ?? null;
  }

  /**
   * Move each value in whichever direction it went, and keep each field over
   * the control it shadows. Called once a frame by the desktop, and again
   * whenever the DOM says one of the fields changed.
   */
  sync(): void {
    // Half a composed character is in the field and none of it is in the
    // control yet, which reads as a fill and is not one.
    if (this.#disposed || this.#host.composing) return;
    let filled = false;
    for (const field of this.#fields) {
      const move = mirrorMove(field.element.value, field.spec.control.text, field.seen);
      if (move?.to === "control") {
        field.spec.control.text = move.value;
        filled = true;
      } else if (move?.to === "field") {
        field.element.value = move.value;
      }
      // Read both ends back: a TextBox sanitizes what it is handed, so what it
      // now holds is not always what arrived.
      field.seen = { field: field.element.value, control: field.spec.control.text };

      const control = field.spec.control;
      const visible = control.effectivelyVisible;
      if (visible !== field.visible) {
        field.visible = visible;
        field.element.style.visibility = visible ? "visible" : "hidden";
      }
      if (!visible) continue;

      const box = overlayBox(control.screenBounds, this.#host.uiScale);
      const was = field.box;
      if (!was || was.left !== box.left || was.top !== box.top || was.width !== box.width || was.height !== box.height) {
        field.box = box;
        const s = field.element.style;
        s.left = `${box.left}px`;
        s.top = `${box.top}px`;
        s.width = `${box.width}px`;
        s.height = `${box.height}px`;
      }
    }
    if (filled) this.#host.invalidate();
  }

  /**
   * Ask the browser to remember what was signed in with.
   *
   * A page that never navigates gives a password manager nothing to notice, so
   * the credential is handed over explicitly. Chromium only - which is every
   * browser that runs Webwin at all - and a refusal is not an error: the
   * manager may be off, or the person may have said no to it before.
   */
  async save(): Promise<boolean> {
    const id = this.#valueOf("username");
    // A change-password form carries both, and the one worth remembering is
    // the one the account has from now on.
    const password = this.#valueOf("new-password") ?? this.#valueOf("current-password");
    if (!id || !password) return false;
    const ctor = (globalThis as { PasswordCredential?: PasswordCredentialCtor }).PasswordCredential;
    if (!ctor || !navigator.credentials?.store) return false;
    try {
      await navigator.credentials.store(new ctor({ id, password, name: id }));
      return true;
    } catch {
      return false;
    }
  }

  get disposed(): boolean {
    return this.#disposed;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    for (const field of this.#fields) {
      field.element.removeEventListener("input", this.#onFieldInput);
      field.element.removeEventListener("change", this.#onFieldInput);
      this.#host.releaseKeyboardHost(field.element);
    }
    this.#fields = [];
    this.element.removeEventListener("submit", this.#onSubmit);
    this.element.remove();
  }

  #valueOf(purpose: AutofillPurpose): string | null {
    const field = this.#fields.find((f) => f.spec.purpose === purpose);
    return field && field.element.value ? field.element.value : null;
  }

  #onSubmit = (e: Event): void => {
    e.preventDefault();
  };

  /**
   * A fill arrives at a field nobody is typing into, and the frame loop is
   * idle when it does - so ask for a frame, and let that frame's `sync` take
   * it.
   *
   * Taking it *here* is what the first draft did, and it doubled every word
   * typed: this is the same `input` event `KeyboardBridge` turns into an edit,
   * both handlers are on the same element, and a microtask queued from one
   * listener runs before the next listener is even called. A frame is the one
   * moment that is certainly after the whole event, whichever order the
   * listeners went on in.
   */
  #onFieldInput = (): void => {
    this.#host.invalidate();
  };
}
